import { useRef, useEffect, useCallback } from 'react';

/**
 * Configuration for the useInfiniteScroll hook
 * @template T Type of the HTML element being observed (must extend HTMLElement)
 */
type UseInfiniteScrollProps<T extends HTMLElement> = {
  /**
   * Reference to the scrollable container.
   * If not provided, the hook will create and return an internal ref.
   */
  ref?: React.RefObject<T>;

  /**
   * Callback function triggered when the user scrolls to the bottom threshold
   * of the scrollable container
   */
  onBottomReached?: () => void;

  /**
   * Distance in pixels from the bottom of the container at which
   * the onBottomReached callback will be triggered
   * @default 0
   */
  threshold?: number;

  /**
   * Controls whether the scroll listener is active
   * @default true
   */
  enabled?: boolean;
};

/**
 * A hook that detects when a user has scrolled to the bottom of a container
 * and executes a callback function. Useful for implementing infinite scrolling.
 *
 * @template T Type of the HTML element being observed (must extend HTMLElement)
 * @param props Configuration object for the infinite scroll behavior
 * @returns Object containing a ref to attach to the scrollable element (if no ref was provided)
 *
 * @example
 * // Basic usage with internal ref
 * const { ref } = useInfiniteScroll({
 *   onBottomReached: () => loadMoreData()
 * });
 *
 * // Render: <div ref={ref}>...</div>
 *
 * @example
 * // Usage with external ref
 * const scrollContainerRef = useRef(null);
 * useInfiniteScroll({
 *   ref: scrollContainerRef,
 *   onBottomReached: () => loadMoreData(),
 *   threshold: 200, // Load more when within 200px of bottom
 * });
 */
export function useInfiniteScroll<T extends HTMLElement>({
  ref,
  onBottomReached,
  threshold = 0,
  enabled = true,
}: UseInfiniteScrollProps<T>) {
  const innerRef = useRef<T>(null);
  const targetRef = ref ?? innerRef;

  const handleScroll = useCallback(() => {
    const el = targetRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

    if (distanceFromBottom <= threshold) {
      onBottomReached?.();
    }
  }, [targetRef, threshold, onBottomReached]);

  useEffect(() => {
    if (!enabled) return undefined;

    const el = targetRef.current;
    /* istanbul ignore next -- @preserve */
    if (!el) return undefined;

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [enabled, targetRef, handleScroll]);

  return { ref: targetRef };
}
